import {
  ConsumerApiSettings,
  ALLOWED_MARKETS,
  LanguageCode,
  MarketCode,
} from '@silvertours/common-landingpages-view';
import { http } from '@silvertours/front-shared';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import { SearchSuggestion, SuggestProviderInterface } from './types';

declare global {
  interface Window {
    newrelic: any;
  }
}

function assertIsValidMarket(market: MarketCode): asserts market is MarketCode {
  if (!ALLOWED_MARKETS.includes(market)) {
    throw new Error(`Value ${market} is not a MarketCode`);
  }
}

const getXWhitelabel = (market: MarketCode) => {
  assertIsValidMarket(market);
  switch (market) {
    case 'DE':
      return 'DE_billiger-mietwagen';
    case 'FR':
      return 'FR_carigami';
    default:
      assertIsValidMarket(market);
      return `${market}_floyt`;
  }
};

type SuggestionResponse = {
  bm_code: string;
  city_name: string;
  country_name: string;
  id: string;
  image_path: string;
  latitude: string;
  longitude: string;
  min_price: string;
  state_name: string;
  title: string;
  html_title: string;
  type: string;
  type_display: string;
  airport_code: string;
  rail_code: string;
};

class SuggestProvider implements SuggestProviderInterface {
  suggestClient: AxiosInstance;

  consumerApiSettings: ConsumerApiSettings;

  options: AxiosRequestConfig;

  token: string | null = null;

  private searchSuggestionsCache: { [key: string]: SearchSuggestion[] } = {};

  private latestPendingRequestId = 0;

  private latestFinishedRequestId = 0;

  private readonly abortControllers: AbortController[] = [];

  private readonly maxAbortControllers = 3;

  private static instance: SuggestProviderInterface;

  // Disable for now, as sending any custom header leads to addtional CORS preflight requests.
  // Currently the API does not use the market anyway.
  private readonly sendMarketHeader = false;

  private constructor(
    consumerApiSettings: ConsumerApiSettings,
    language: LanguageCode,
    market: MarketCode,
  ) {
    this.consumerApiSettings = consumerApiSettings;
    this.options = {
      headers: {
        // Don't send client-id to prevent additional CORS preflight requests
        'client-id': undefined,
        'X-Whitelabel': this.sendMarketHeader
          ? getXWhitelabel(market)
          : undefined,
        'Accept-Language': language,
      },
    };
    this.suggestClient = http.getApi(
      consumerApiSettings.host,
      { ...this.options },
      '',
    );
  }

  public static getInstance(
    consumerApiSettings: ConsumerApiSettings,
    language: LanguageCode,
    market: MarketCode,
  ) {
    if (!this.instance) {
      this.instance = new this(consumerApiSettings, language, market);
    }
    return this.instance;
  }

  keyExistsInCache(key: string): boolean {
    return key in this.searchSuggestionsCache;
  }

  async sendRequest(
    searchString: string,
  ): Promise<Array<SearchSuggestion> | null> {
    if (this.keyExistsInCache(searchString)) {
      return this.searchSuggestionsCache[searchString];
    }

    while (
      this.abortControllers.length &&
      this.abortControllers.length >= this.maxAbortControllers
    ) {
      // Remove the oldest abort controller if we exceed the max limit
      const oldestController = this.abortControllers.shift();
      if (oldestController) {
        oldestController.abort();
      }
    }

    const abortController = new AbortController();
    this.abortControllers.push(abortController);

    const { signal } = abortController;

    // Increment and capture the request id for this invocation
    this.latestPendingRequestId += 1;
    const requestId = this.latestPendingRequestId;

    try {
      const result = await this.suggestClient.request({
        method: 'get',
        url: this.consumerApiSettings.locationSuggest,
        withCredentials: false, // exclude cookies to reduce request payload size
        params: {
          query: searchString,
          context: 'car_rental',
        },
        signal,
      });

      if (signal.aborted) {
        return null;
      }

      const suggestionResults = (result.data as Array<any>).map(
        ({
          bm_code,
          city_name,
          country_name,
          id,
          image_path,
          latitude,
          longitude,
          min_price,
          state_name,
          title,
          html_title,
          type,
          type_display,
          airport_code,
          rail_code,
        }: SuggestionResponse) =>
          ({
            bmCode: bm_code,
            cityName: city_name,
            countryName: country_name,
            id,
            image_path,
            latitude,
            longitude,
            minPrice: min_price,
            objectID: id,
            stateName: state_name,
            title,
            htmlTitle: html_title,
            type,
            typeDisplay: type_display,
            airportCode: airport_code,
            railCode: rail_code,
          }) as SearchSuggestion,
      );

      this.searchSuggestionsCache[searchString] = suggestionResults;

      if (requestId < this.latestFinishedRequestId) {
        return null;
      }

      this.latestFinishedRequestId = requestId;

      return suggestionResults;
    } catch (e: unknown) {
      if (axios.isCancel?.(e)) {
        // Request was aborted, do not handle as error
        return null;
      }
      if (e && axios.isAxiosError(e)) {
        // eslint-disable-next-line no-console
        console.error(
          'Request error when fetching suggestions',
          e.message,
          searchString,
        );
      }

      if (typeof window !== 'undefined') {
        const { newrelic } = window;
        newrelic?.noticeError(e);
      }
      return null;
    }
  }
}

export { SuggestProvider };
